/**
 * OscillatorNode - simple oscillator using Web Audio API
 *
 * Endpoints:
 * - frequency (input): frequency in Hz
 * - waveform (input): sine, sawtooth, triangle, square
 * - audioOut (output): audio output
 *
 * Simple test node for audio routing.
 */

import { AudioBaseNode, type AudioDependencies } from '../AudioBaseNode';
import { Domain } from '@nodes/Endpoint';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import { audio, params } from '@nodes/TypedEndpoints';

const endpoints = {
  ...params({
    frequency: { dataType: 'number', value: 440, min: 20, max: 2000 },
    waveform: { dataType: 'string', value: 'sine', options: ['sine', 'sawtooth', 'triangle', 'square'] as const },
    syncToTransport: {
      label: 'sync', info: 'Start/stop with transport',
      dataType: 'boolean', value: true
    },
    playing: { dataType: 'boolean', value: false }
  }),
  ...audio({
    audioOut: { isOutput: true }
  })
};

export class OscillatorNode extends AudioBaseNode<typeof endpoints> {
  private osc: any = null;

  constructor(id: string, deps: AudioDependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();
    this.subscribeToTransport();
  }

  onParamChanged(payload: Payload, endpointId: string): void {
    switch (endpointId) {
      case 'frequency':
        if (this.osc) {
          this.osc.frequency.value = payload.value;
        }
        break;
      case 'waveform':
        if (this.osc) {
          this.osc.type = payload.value as OscillatorType;
        }
        break;
      case 'syncToTransport':
        if (payload.value) {
          const shouldPlay = this.transport.state === 'started';
          this.params.playing.set(shouldPlay);
          if (shouldPlay) this.start(); else this.stop();
        }
        break;
      case 'playing':
        if (payload.value) {
          this.start();
        } else {
          this.stop();
        }
        break;
    }
  }

  start(): void {
    if (this.osc) return; // Already playing

    this.osc = this.audioContext.createOscillator();
    this.osc.type = this.params.waveform.value as OscillatorType;
    this.osc.frequency.value = this.params.frequency.value;

    this.audio.audioOut.connect(this.osc);
    this.osc.start();

    this.logTrace(`started: ${this.osc.type} @ ${this.osc.frequency.value}Hz`);
  }

  stop(): void {
    if (!this.osc) return;

    this.osc.stop();
    this.osc.disconnect();
    this.osc = null;

    this.logTrace('stopped');
  }

  dispose(): void {
    this.stop();
    this.unsubscribeFromTransport();
    super.dispose();
  }

  private subscribeToTransport(): void {
    this.transport.on('start', this.boundStart);
    this.transport.on('stop', this.boundStop);
    this.transport.on('pause', this.boundStop);
  }

  private unsubscribeFromTransport(): void {
    this.transport.off('start', this.boundStart);
    this.transport.off('stop', this.boundStop);
    this.transport.off('pause', this.boundStop);
  }

  private boundStart = () => {
    if (this.params.syncToTransport.value) {
      this.params.playing.set(true);
      this.start();
    }
  };
  private boundStop = () => {
    if (this.params.syncToTransport.value) {
      this.params.playing.set(false);
      this.stop();
    }
  };
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const oscillatorNodeDef: NodeDef<OscillatorNode> = {
  type: 'Oscillator',
  shortName: 'osc',
  info: 'Simple Web Audio oscillator. Sine, sawtooth, triangle, or square waveforms.',
  domain: Domain.Audio,
  endpoints,
  ctor: OscillatorNode
};
